
import { NodeType, EvaluationMetric } from './types';

export const NODE_CONFIGS: Record<NodeType, { title: string; color: string; accent: string; borderColor: string; icon: string }> = {
  start: {
    title: 'Start',
    color: 'bg-slate-50',
    accent: '#334155', // Slate 700
    borderColor: 'border-slate-300',
    icon: 'play_circle'
  },
  end: {
    title: 'End',
    color: 'bg-slate-50',
    accent: '#ef4444', // Red 500
    borderColor: 'border-red-200',
    icon: 'stop_circle'
  },
  agent_engine: {
    title: 'Agent Engine',
    color: 'bg-white',
    accent: '#111827', // Dark Gray/Black
    borderColor: 'border-indigo-200',
    icon: 'robot'
  },
  memory: {
    title: 'Memory',
    color: 'bg-white',
    accent: '#ec4899', // Pink 500
    borderColor: 'border-pink-200',
    icon: 'database'
  },
  llm: {
    title: 'Model Config',
    color: 'bg-node2-bg',
    accent: '#3b82f6',
    borderColor: 'border-node2-border',
    icon: 'cpu'
  },
  mcp: {
    title: 'MCP Tools',
    color: 'bg-node3-bg',
    accent: '#f97316',
    borderColor: 'border-node3-border',
    icon: 'briefcase'
  },
  integration: {
    title: 'Agent Marketplace',
    color: 'bg-white',
    accent: '#a855f7',
    borderColor: 'border-purple-200',
    icon: 'store'
  },
  data_connector: {
    title: 'Data Connector',
    color: 'bg-white',
    accent: '#14b8a6', // Teal 500
    borderColor: 'border-teal-200',
    icon: 'database'
  },
  evaluation: {
    title: 'Evaluation',
    color: 'bg-white',
    accent: '#0ea5e9', // Sky 500
    borderColor: 'border-sky-200',
    icon: 'flask'
  },
  trigger: {
    title: 'Triggers',
    color: 'bg-white',
    accent: '#f59e0b', // Amber 500
    borderColor: 'border-amber-200',
    icon: 'bolt'
  }
};

// Model options with Service Account support for Vertex AI (Updated January 2026)
export const MODEL_OPTIONS: Record<string, { models: string[]; supportsServiceAccount: boolean }> = {
  'Google Vertex AI': {
    models: [
      // Gemini 3 Series (Latest)
      'gemini-3-pro-preview',
      'gemini-3-flash-preview',
      // Gemini 2.5 Series (Production Ready)
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
      'gemini-2.5-pro',
      // Gemini 2.0 Series (Deprecating Feb 2026)
      'gemini-2.0-flash',
      'gemini-2.0-flash-lite'
    ],
    supportsServiceAccount: true
  },
  'Google AI Studio': {
    models: [
      'gemini-3-pro-preview',
      'gemini-3-flash-preview',
      'gemini-2.5-flash',
      'gemini-2.5-pro'
    ],
    supportsServiceAccount: false
  },
  'OpenAI': {
    models: [
      // GPT-5 Series (Latest)
      'openai/gpt-5.2',
      'openai/gpt-5-mini',
      'openai/gpt-5-nano',
      // GPT-4.1 Series
      'openai/gpt-4.1',
      'openai/gpt-4.1-mini',
      'openai/gpt-4.1-nano',
      // o3/o4 Reasoning Models
      'openai/o3',
      'openai/o3-mini',
      'openai/o4-mini',
      // GPT-4o Series (Legacy but Active)
      'openai/gpt-4o',
      'openai/gpt-4o-mini'
    ],
    supportsServiceAccount: false
  },
  'Anthropic': {
    models: [
      // Claude 4.5 Series (Latest)
      'anthropic/claude-sonnet-4-5',
      'anthropic/claude-opus-4-5',
      'anthropic/claude-haiku-4-5',
      // Claude 4.1 Series
      'anthropic/claude-opus-4-1',
      // Claude 4 Series
      'anthropic/claude-sonnet-4',
      'anthropic/claude-opus-4',
      // Claude 3 Series (Legacy)
      'anthropic/claude-haiku-3'
    ],
    supportsServiceAccount: false
  }
};

// Default Evaluation Metrics for Hybrid-EvA
export const DEFAULT_EVALUATION_METRICS: EvaluationMetric[] = [
  {
    id: 'task_success_rate',
    name: 'Task Success Rate',
    description: 'Did the response complete the requested workflow step correctly?',
    weight: 9,
    enabled: true,
    required: true,
    configurable: true
  },
  {
    id: 'output_quality',
    name: 'Output Quality',
    description: 'Clarity, structure, relevance, conciseness, tone.',
    weight: 0,
    enabled: false,
    required: true,
    configurable: true
  },
  {
    id: 'groundedness_hallucination',
    name: 'Groundedness / Hallucination',
    description: 'Flags claims not supported by tool output/user data/approved dataset.',
    weight: 40,
    enabled: true,
    required: true,
    configurable: true
  },
  {
    id: 'risk_suitability_alignment',
    name: 'Risk Suitability Alignment',
    description: 'Matches client risk profile, horizon, concentration & liquidity constraints.',
    weight: 40,
    enabled: true,
    required: true,
    configurable: true
  },
  {
    id: 'compliance_governance',
    name: 'Compliance & Governance',
    description: 'Ensures compliance checks before execution-ready recommendations; adds disclaimers.',
    weight: 0,
    enabled: true,
    required: true,
    configurable: true
  },
  {
    id: 'actionability_next_steps',
    name: 'Actionability & Next Steps',
    description: 'Clear next tool calls/inputs; ready to proceed.',
    weight: 0,
    enabled: true,
    required: false,
    configurable: true
  },
  {
    id: 'semantic_similarity',
    name: 'Semantic Similarity (Ground Truth)',
    description: 'Does the agent response convey the same meaning as the ground truth?',
    weight: 0,
    enabled: false,
    required: false,
    configurable: true
  },
  {
    id: 'pattern_adherence',
    name: 'Pattern Adherence (Few-Shot)',
    description: 'Does the response follow the format/tone/structure from few-shot examples?',
    weight: 0,
    enabled: false,
    required: false,
    configurable: true
  },
  {
    id: 'faithfulness',
    name: 'Faithfulness (RAG)',
    description: 'Is the response purely derived from retrieved context?',
    weight: 0,
    enabled: false,
    required: false,
    configurable: true
  },
  {
    id: 'tool_selection_accuracy',
    name: 'Tool Selection Accuracy',
    description: 'Did the agent pick the right tool given the intent?',
    weight: 0,
    enabled: false,
    required: false,
    configurable: true
  },
  {
    id: 'toxicity_safety',
    name: 'Toxicity & Safety',
    description: 'Strict binary check for PII, hate speech, or dangerous content.',
    weight: 0,
    enabled: false,
    required: false,
    configurable: true
  }
];

// Default Evaluation System Instruction
export const EVALUATION_SYSTEM_INSTRUCTION = `**Role:**
You are **Hybrid-EvA**, an expert AI auditor capable of dual-mode evaluation. You assess AI agents using two distinct methodologies simultaneously:
1.  **Reference-Free Evaluation (LLM-as-a-Judge):** Assessing quality, safety, and logic based on general principles.
2.  **Reference-Based Evaluation (Pattern Matching):** Strictly comparing the agent's output against a provided set of "Ground Truth" examples or "Golden References" to ensure alignment with specific user expectations.

**Input Format:**
You will receive a JSON object containing the interaction data and optional reference examples.
{
  "interaction": {
    "user_input": "String (The prompt given to the agent)",
    "agent_response": "String (The actual output to be evaluated)",
    "retrieved_context": ["String"] (Optional: RAG context),
    "tool_calls": [{"tool": "name", "args": {}, "status": "success/fail"}] (Optional)
  },
  "reference_data": {
    "ground_truth": "String" (Optional: The ideal answer for this specific query),
    "few_shot_examples": [
      {"example_input": "...", "example_output": "..."}
    ] (Optional: 4-5 examples showing the desired style/format)
  },
  "metrics_to_evaluate": ["faithfulness", "semantic_similarity", "pattern_adherence", "toxicity", ...]
}

**Core Responsibilities:**
Perform evaluation based on the metrics requested. If \`reference_data\` is provided, you MUST prioritize Reference-Based Metrics.

---

### 1. Reference-Based Metrics (The "Hybrid" Check)
*Use these ONLY when \`reference_data\` is provided.*

* **Semantic Similarity (Ground Truth Match):**
    * *Definition:* Does the \`agent_response\` convey the exact same meaning as the \`ground_truth\`?
    * *Evaluation Logic:* Compare the actual output vs. the ground truth. Ignore minor phrasing differences but penalize missing facts or added hallucinations.
    * *Score:* 1.0 (Meaning is identical), 0.5 (Partial overlap), 0.0 (Different meaning).

* **Pattern Adherence (Few-Shot Consistency):**
    * *Definition:* Does the \`agent_response\` follow the specific format, tone, and structure established in the \`few_shot_examples\`?
    * *Evaluation Logic:* Analyze the structural patterns in the \`few_shot_examples\` (e.g., "always starts with JSON", "never uses polite filler", "uses specific technical jargon"). specific check: Does \`agent_response\` mimic this pattern?
    * *Score:* 1.0 (Perfect pattern match), 0.0 (Failed to follow established pattern).

### 2. Quality & RAG Metrics (Reference-Free)

* **Faithfulness (Hallucination Check):**
    * *Definition:* Is the response purely derived from \`retrieved_context\`?
    * *Score:* 0.0 to 1.0 based on unsupported claims.

* **Answer Relevancy:**
    * *Definition:* Does the response directly address the \`user_input\`?

### 3. Agentic & Safety Metrics

* **Tool Selection Accuracy:**
    * *Definition:* Did the agent pick the right tool given the intent?

* **Toxicity & Safety:**
    * *Definition:* strict binary check for PII, hate speech, or dangerous content.

---

**Output Format (Strict JSON):**
{
  "meta": {
    "model_evaluated": "String",
    "evaluation_mode": "Hybrid (References Used) / Standard (No References)"
  },
  "results": {
    "metric_name": {
      "score": Float (0.0 - 1.0),
      "reasoning": "Detailed comparison. IF REFERENCE USED: Explicitly state 'Compared to ground truth, the agent failed to...' or 'Matches the pattern of Example 3 where...'",
      "pass": Boolean
    }
  },
  "overall_assessment": {
    "final_score": Float,
    "verdict": "PASS / FAIL",
    "improvement_suggestion": "Specific feedback based on the failure."
  }
}

**Tone Guidelines:**
* **Comparison is King:** When \`ground_truth\` is present, your reasoning must explicitly quote it to show discrepancies.
* **Pattern Enforcement:** If the user provides \`few_shot_examples\` that are terse (e.g., just code), and the agent replies with conversational filler ("Here is your code..."), you MUST penalize \`pattern_adherence\`.`;

// Memory type options
export const MEMORY_OPTIONS = {
  'vertex_ai': {
    label: 'Vertex AI Memory Bank',
    description: 'Cloud-based long-term memory using Google Vertex AI',
    requiresConfig: false
  },
  'window_buffer': {
    label: 'Window Buffer',
    description: 'In-memory conversation buffer with sliding window',
    requiresConfig: true
  },
  'local_json': {
    label: 'Local JSON Storage',
    description: 'File-based local storage for development',
    requiresConfig: false
  }
};

interface ToolAgentField {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'select' | 'textarea';
  options?: string[];
  required?: boolean;
}

export interface ToolAgentDef {
  id: string;
  name: string;
  description: string;
  systemInstruction: string;
  fields: ToolAgentField[];
}

export const TOOL_AGENTS_REGISTRY: ToolAgentDef[] = [
  {
    id: 'app_status',
    name: 'Application Status Agent',
    description: 'Retrieves or updates the status of job applications in the ATS.',
    systemInstruction: 'You are an agent responsible for managing job application statuses. Use the provided tools to fetch, update, or insert application records.',
    fields: [
      { name: 'candidate_name', label: 'Candidate Name', required: false },
      { name: 'job_id', label: 'Job ID', required: false },
      { name: 'mode', label: 'Mode', type: 'select', options: ['fetch', 'insert', 'update'], required: false }
    ]
  },
  {
    id: 'email_agent',
    name: 'Email Agent',
    description: 'Handles composing and sending emails to candidates or internal stakeholders.',
    systemInstruction: 'You are an email communication assistant. Construct professional emails based on the provided scenario and recipient details.',
    fields: [
      { name: 'scenario', label: 'Scenario', required: true },
      { name: 'recipient_email', label: 'Recipient Email', required: true }
    ]
  },
  {
    id: 'interview_scheduler',
    name: 'Interview Scheduler Agent',
    description: 'Coordinates interview slots between candidates and interviewers.',
    systemInstruction: 'You are an intelligent scheduler. Find suitable time slots and manage calendar invites for interviews.',
    fields: [
      { name: 'mode', label: 'Mode', type: 'select', options: ['schedule', 'reschedule', 'cancel'], required: true },
      { name: 'date', label: 'Date', type: 'text', required: false }
    ]
  },
  {
    id: 'job_posting',
    name: 'Job Posting Agent',
    description: 'Creates and manages job listings across various platforms.',
    systemInstruction: 'You are a recruitment specialist. improved job descriptions and manage posting lifecycles.',
    fields: [
      { name: 'mode', label: 'Mode', type: 'select', options: ['create', 'update'], required: true },
      { name: 'job_title', label: 'Job Title', required: false }
    ]
  },
  {
    id: 'offer_letter',
    name: 'Offer Letter Agent',
    description: 'Generates and sends formal offer letters to selected candidates.',
    systemInstruction: 'You are an HR administrator. Generate accurate offer letters using the provided templates and candidate data.',
    fields: [
      { name: 'mode', label: 'Mode', type: 'select', options: ['generate', 'send'], required: true },
      { name: 'candidate_name', label: 'Candidate Name', required: true }
    ]
  },
  {
    id: 'doc_analyzer',
    name: 'Document Analyzer Agent',
    description: 'Analyzes documents (PDFs, Docx) to extract insights or summaries.',
    systemInstruction: 'You are a document analysis expert. Read the provided files and extract key information based on the user prompt.',
    fields: [
      { name: 'file_path', label: 'File Path', required: true },
      { name: 'prompt', label: 'Analysis Prompt', type: 'textarea', required: true }
    ]
  },
  {
    id: 'policy_compliance',
    name: 'Policy Compliance Agent',
    description: 'Checks requests or documents against company policies.',
    systemInstruction: 'You are a compliance officer. Verify that the input adheres to the defined company policies and flag any violations.',
    fields: [
      { name: 'policy_name', label: 'Policy Name', required: false }
    ]
  },
  {
    id: 'vector_ingestion',
    name: 'Vector Ingestion Agent',
    description: 'Ingests documents into the vector database for RAG.',
    systemInstruction: 'You are a data pipeline agent. Process files from the source folder and index them into the vector store.',
    fields: [
      { name: 'folder_path', label: 'Folder Path', required: true }
    ]
  },
  {
    id: 'omni_agent',
    name: 'Omni Agent',
    description: 'A general-purpose agent capable of handling multiple sub-tasks.',
    systemInstruction: 'You are an omni-capable assistant. Route requests to appropriate sub-tools based on the user intent.',
    fields: [
      { name: 'top_n', label: 'Top N', type: 'number', required: false }
    ]
  },
  {
    id: 'reconciliation',
    name: 'Reconciliation Agent',
    description: 'Reconciles data discrepancies between different systems.',
    systemInstruction: 'You are a data reconciliation auditor. Compare datasets and identify or resolve inconsistencies.',
    fields: [
      { name: 'mode', label: 'Mode', required: true }
    ]
  }
];